"use client";

/** In-browser lookups over the CC0 Lichess opening catalogue (public/data/openings-lines.json). */

export type OpeningRow = [eco: string, name: string, uci: string];

let loading: Promise<OpeningRow[]> | null = null;

export function loadOpenings(): Promise<OpeningRow[]> {
  loading ??= fetch("/data/openings-lines.json")
    .then((r) => (r.ok ? r.json() : []))
    .catch(() => []);
  return loading;
}

/** Name of the deepest catalogued line that the moves so far follow exactly. */
export function openingName(rows: OpeningRow[], moves: string[]): { eco: string; name: string; plies: number } | null {
  let best: { eco: string; name: string; plies: number } | null = null;
  const joined = moves.join(" ");
  for (const [eco, name, uci] of rows) {
    if (!uci) continue;
    const plies = uci.split(" ").length;
    if (plies > moves.length) continue;
    if (joined === uci || joined.startsWith(uci + " ")) {
      if (!best || plies > best.plies) best = { eco, name, plies };
    }
  }
  return best;
}

/** How many leading plies of a game are known opening theory. */
export function bookDepth(rows: OpeningRow[], moves: string[]): number {
  return openingName(rows, moves)?.plies ?? 0;
}

/** Named continuations from the current position, grouped by next move. */
export function continuations(rows: OpeningRow[], moves: string[]): { uci: string; names: string[]; lines: number }[] {
  const prefix = moves.join(" ");
  const next = new Map<string, Set<string>>();
  for (const [, name, uci] of rows) {
    if (!(prefix === "" || uci.startsWith(prefix + " "))) continue;
    const rest = prefix === "" ? uci : uci.slice(prefix.length + 1);
    const mv = rest.split(" ")[0];
    if (!mv) continue;
    if (!next.has(mv)) next.set(mv, new Set());
    next.get(mv)!.add(name);
  }
  return [...next.entries()]
    .map(([uci, names]) => ({ uci, names: [...names].sort((a, b) => a.length - b.length), lines: names.size }))
    .sort((a, b) => b.lines - a.lines);
}
